import {
  Container, Title, Text, Card, Group, ThemeIcon, Stack, Grid, Badge, Divider,
} from '@/components/ui';
import {
  IconSparkles, IconHistory, IconMessageChatbot, IconRobot,
  IconBrain, IconFileText, IconArrowRight,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

const features = [
  {
    title: 'Start Workflow',
    description: 'Create records from images, audio, or text using AI extraction.',
    icon: IconSparkles,
    color: 'violet',
    path: '/app/ai/workflow',
  },
  {
    title: 'Past Sessions',
    description: 'View and manage your previous AI workflow sessions.',
    icon: IconHistory,
    color: 'blue',
    path: '/app/ai/sessions',
  },
  {
    title: 'Document Q&A',
    description: 'Ask questions about your documents using AI. Coming soon.',
    icon: IconMessageChatbot,
    color: 'teal',
    path: '',
    disabled: true,
  },
];

export default function AiLandingPage() {
  const navigate = useNavigate();

  return (
    <Container fluid py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" wrap="wrap">
          <div>
            <Title order={2}>AI Management</Title>
            <Text size="sm" c="dimmed">
              Leverage AI to automate record creation and data extraction.
            </Text>
          </div>
        </Group>

        {/* Quick-action stat cards */}
        <Grid>
          <Grid.Col span={{ base: 6, md: 4 }}>
            <Card
              shadow="sm"
              padding="lg"
              radius="md"
              withBorder
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/app/ai/sessions')}
            >
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="blue">
                  <IconHistory size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>My Sessions</Text>
                  <Text size="sm" c="dimmed">View past workflows</Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 4 }}>
            <Card
              shadow="sm"
              padding="lg"
              radius="md"
              withBorder
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/app/ai/documents')}
            >
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="teal">
                  <IconFileText size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Documents Indexed</Text>
                  <Text size="sm" c="dimmed">Manage your library</Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 6, md: 4 }}>
            <Card
              shadow="sm"
              padding="lg"
              radius="md"
              withBorder
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/app/ai/workflow')}
            >
              <Group wrap="nowrap">
                <ThemeIcon size={48} radius="md" variant="light" color="violet">
                  <IconBrain size={24} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Workflows Available</Text>
                  <Text size="sm" c="dimmed">Start a new workflow</Text>
                </div>
              </Group>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Feature navigation cards */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <IconRobot size={20} />
              <Title order={4}>AI Features</Title>
            </Group>
            <Badge variant="light" color="violet">AI Powered</Badge>
          </Group>
          <Divider mb="sm" />

          <Grid>
            {features.map((feature) => (
              <Grid.Col key={feature.title} span={{ base: 12, sm: 6, md: 4 }}>
                <Card
                  shadow="sm"
                  padding="lg"
                  radius="md"
                  withBorder
                  style={{
                    cursor: feature.disabled ? 'not-allowed' : 'pointer',
                    opacity: feature.disabled ? 0.6 : 1,
                  }}
                  onClick={() => {
                    if (!feature.disabled && feature.path) {
                      navigate(feature.path);
                    }
                  }}
                >
                  <Group gap="md" mb="sm">
                    <ThemeIcon size="lg" radius="md" variant="light" color={feature.color}>
                      <feature.icon size={20} />
                    </ThemeIcon>
                    <Text fw={600} size="lg">
                      {feature.title}
                    </Text>
                  </Group>
                  <Text size="sm" c="dimmed">
                    {feature.description}
                  </Text>
                  {feature.disabled ? (
                    <Text size="xs" c="dimmed" mt="sm" fs="italic">
                      Coming soon
                    </Text>
                  ) : (
                    <Group mt="sm" gap="xs">
                      <Text size="xs" c={feature.color} fw={500}>Get started</Text>
                      <IconArrowRight size={14} color={`var(--mantine-color-${feature.color}-6)`} />
                    </Group>
                  )}
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        </Card>
      </Stack>
    </Container>
  );
}
